import {
  Button,
  Input,
  message,
  Popconfirm,
  Select,
  Spin,
  Table,
  TableColumnsType,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getUserDetails,
  makeGetCall,
  makePostCall,
} from '../../utils/helper';
import { useSites } from '../../hooks/useSites';
import './manage-nodes.css';
import { CloseCircleOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import EditNode from './EditNode';
import ErrorBoundary from '../../components/ErrorBoundary';

const { Search } = Input;
const { Option } = Select;

// Type definitions

interface Modality {
  en_id: string;
  en_node_name: string;
  en_ip_address: string;
  en_port: string;
  en_ae_title: string;
  en_location: string;
  en_operations: string;
  en_comments: string;
  en_modality?: string | null;
}

interface ModalityListState {
  loading: boolean;
  data: Modality[];
  error: string | null;
}

interface APIResponse<T> {
  data?: {
    data?: T;
    success?: boolean;
    message?: string;
  };
  success?: boolean;
  message?: string;
}

interface UserDetails {
  username: string;
} // Modal for editing modality

/**
 * ModalityList Component
 *
 * A comprehensive modality management interface that provides:
 * - List view of all DICOM modalities/nodes
 * - Search and filtering capabilities
 * - CRUD operations (Create, Read, Update, Delete)
 * - Error handling and loading states
 * - Responsive design with accessibility features
 *
 * @component
 * @returns {JSX.Element} The modality list management interface
 */
const ModalityList: React.FC = () => {
  // Ref to track component mount status for cleanup
  const isMountedRef = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [modalityList, setModalityList] = useState<ModalityListState>({
    loading: true,
    data: [],
    error: null
  });
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [selectedModality, setSelectedModality] = useState<Modality | null>(null);
  const { centers, loading: unitsLoading, error: unitsError } = useSites();

  const unitsMapping = useMemo((): Record<string, string> => {
    return centers.reduce((acc, center) => {
      if (center.value && center.label) {
        acc[center.value] = center.label;
      }
      return acc;
    }, {} as Record<string, string>);
  }, [centers]);

  // Filter states
  const [searchText, setSearchText] = useState<string>('');
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<string | null>(null);

  /**
   * Fetches the list of modalities from the server
   *
   * @async
   * @function fetchModalities
   * @returns {Promise<void>} Promise that resolves when modalities are loaded
   * @throws {Error} When API call fails or returns invalid data
   */
  const fetchModalities = useCallback(async (): Promise<void> => {
    try {
      setModalityList({ loading: true, data: [], error: null });

      const res: APIResponse<Modality[]> = await makeGetCall('/get-modality-list');

      if (res.data?.success !== false && Array.isArray(res.data?.data)) {
        if (isMountedRef.current) {
          setModalityList({
            data: res.data.data,
            loading: false,
            error: null
          });
        }
      } else {
        throw new Error(res.data?.message || 'Invalid response format');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load modalities';
      console.error('Error fetching modalities:', error);

      if (isMountedRef.current) {
        setModalityList({
          data: [],
          loading: false,
          error: errorMessage
        });
      }

      message.error(`Error loading modalities: ${errorMessage}`);
    }
  }, []);

  useEffect(() => {
    // Cancel previous requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    fetchModalities();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchModalities]);

  /**
   * Deletes a modality/node from the system
   *
   * @async
   * @function deleteModality
   * @param {Modality} node - The modality object to delete
   * @returns {Promise<void>} Promise that resolves when deletion is complete
   * @throws {Error} When deletion fails or node data is invalid
   */
  const deleteModality = useCallback(async (node: Modality): Promise<void> => {
    if (!node?.en_id) {
      message.error('Invalid modality data');
      return;
    }

    try {
      setDeleteInProgress(true);

      const res: APIResponse<any> = await makePostCall('/delete-modality', {
        en_id: node.en_id
      });

      if (res.data?.success) {
        message.success('Modality deleted successfully');
        await fetchModalities();
      } else {
        throw new Error(res.data?.message || 'Failed to delete modality');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete modality';
      console.error('Error deleting modality:', error);
      message.error(errorMessage);
    } finally {
      setDeleteInProgress(false);
    }
  }, [fetchModalities]);

  const openEditModal = useCallback((record: Modality): void => {
    setSelectedModality(record);
    setEditModalVisible(true);
  }, []);

  /**
   * Handles form submission for editing a modality
   *
   * @async
   * @function handleEditSubmit
   * @param {Partial<Modality & { user_id: string }>} updatedData - Updated modality data
   * @returns {Promise<void>} Promise that resolves when update is complete
   * @throws {Error} When update fails or data validation fails
   */
  const handleEditSubmit = useCallback(async (updatedData: Partial<Modality & { user_id: string }>): Promise<void> => {
    if (!updatedData || typeof updatedData !== 'object') {
      message.error('Invalid form data');
      return;
    }

    try {
      const userDetails: UserDetails = getUserDetails();

      if (!userDetails?.username) {
        throw new Error('User authentication required');
      }

      const res: APIResponse<any> = await makePostCall('/add-modality', {
        ...updatedData,
        user_id: userDetails.username,
      });

      if (res.data?.success) {
        message.success('Modality updated successfully');
        setEditModalVisible(false);
        await fetchModalities();
      } else {
        throw new Error(res.data?.message || 'Failed to update modality');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update modality';
      console.error('Error updating modality:', error);
      message.error(errorMessage);
    }
  }, [fetchModalities]);

  /**
   * Memoized filtered modalities based on search and filter criteria
   *
   * @function filteredModalities
   * @returns {Modality[]} Filtered array of modalities
   */
  const filteredModalities = useMemo((): Modality[] => {
    let filtered = modalityList.data;

    // Filter by search text (name, IP, AE title) - optimized with useMemo
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(modality => {
        const name = (modality.en_node_name || '').toLowerCase();
        const ip = (modality.en_ip_address || '').toLowerCase();
        const aeTitle = (modality.en_ae_title || '').toLowerCase();

        return name.includes(searchLower) ||
               ip.includes(searchLower) ||
               aeTitle.includes(searchLower);
      });
    }

    // Filter by unit
    if (selectedUnit) {
      filtered = filtered.filter(modality => modality.en_location === selectedUnit);
    }

    // Filter by operation
    if (selectedOperation) {
      filtered = filtered.filter(modality => modality.en_operations === selectedOperation);
    }

    return filtered;
  }, [modalityList.data, searchText, selectedUnit, selectedOperation]);

  const handleCloseEditModal = useCallback(() => {
    setEditModalVisible(false);
    setSelectedModality(null);
  }, []);

  // Clear filters function
  const clearFilters = useCallback((): void => {
    setSearchText('');
    setSelectedUnit(null);
    setSelectedOperation(null);
  }, []);

  // Memoized retry handler
  const handleRetry = useCallback(async (): Promise<void> => {
    try {
      await fetchModalities();
    } catch (error) {
      console.error('Error during retry:', error);
    }
  }, [fetchModalities]);

  const COLUMNS = useMemo((): TableColumnsType<Modality> => [
    {
      title: 'S.No',
      key: 'sno',
      width: 64,
      align: 'center',
      render: (_v, _r, index) => <span className="mn-sno">{index + 1}</span>,
    },
    {
      title: 'Name',
      dataIndex: 'en_node_name',
      render: (val) => <span className="mn-name">{val || '—'}</span>,
    },
    {
      title: 'IP / HostName',
      dataIndex: 'en_ip_address',
      render: (val) => <span className="mn-mono">{val || '—'}</span>,
    },
    {
      title: 'Port',
      dataIndex: 'en_port',
      width: 90,
      render: (val) => <span className="mn-mono">{val || '—'}</span>,
    },
    {
      title: 'AE Title',
      dataIndex: 'en_ae_title',
      render: (val) => (val ? <span className="mn-aet">{val}</span> : <span className="mn-dim">—</span>),
    },
    {
      title: 'Modality',
      dataIndex: 'en_modality',
      width: 90,
      render: (val) => (val ? <span>{val}</span> : <span className="mn-dim">Any</span>),
    },
    {
      title: 'Location',
      dataIndex: 'en_location',
      render: (val) => (val && unitsMapping[val]
        ? <span className="mn-loc">{unitsMapping[val]}</span>
        : <span className="mn-dim">NA</span>),
    },
    {
      title: 'Comments',
      dataIndex: 'en_comments',
      ellipsis: true,
      render: (val) => (val ? <span title={val}>{val}</span> : <span className="mn-dim">—</span>),
    },
    {
      title: 'Edit',
      key: 'edit',
      width: 64,
      align: 'center',
      render: (_v, record) => (
        <Button
          className="mn-icon-btn mn-edit"
          icon={<EditOutlined />}
          onClick={() => openEditModal(record)}
          disabled={deleteInProgress}
          title="Edit node"
        />
      ),
    },
    {
      title: 'Delete',
      key: 'delete',
      width: 72,
      align: 'center',
      render: (_v, record) => (
        <Popconfirm
          title="Delete this node?"
          description="This action cannot be undone."
          onConfirm={() => deleteModality(record)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button
            className="mn-icon-btn mn-delete"
            icon={<CloseCircleOutlined />}
            disabled={deleteInProgress}
            title="Delete node"
          />
        </Popconfirm>
      ),
    },
  ], [unitsMapping, openEditModal, deleteModality, deleteInProgress]);

  const isLoading = modalityList.loading || deleteInProgress || unitsLoading;
  const loadingMessage = deleteInProgress ? "Processing modality..." : modalityList.loading ? "Loading modalities..." : "Loading data...";

  return (
    <ErrorBoundary>
      <Spin spinning={isLoading} tip={loadingMessage}>
        <div className="mn-page">
          {(modalityList.error || unitsError) && (
            <div className="error-container">
              <strong>Error:</strong> {modalityList.error || unitsError}
              <Button
                type="link"
                size="small"
                onClick={handleRetry}
                className="retry-button"
              >
                Retry
              </Button>
            </div>
          )}
          <div className="mn-panel">
            {/* Title + filters */}
            <div className="mn-panel-head">
              <div className="mn-panel-title">
                DICOM Nodes
                <span className="mn-count">{filteredModalities.length}</span>
              </div>
              <div className="mn-filters">
                <Search
                  placeholder="Search name, IP, or AE title"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 280 }}
                  prefix={<SearchOutlined />}
                  aria-label="Search nodes by name, IP address, or AE title"
                  allowClear
                />

                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder="Location"
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                  style={{ width: 160 }}
                  allowClear
                  aria-label="Filter nodes by location"
                >
                  {centers.map(center => (
                    <Option key={center.value} value={center.value}>
                      {center.label}
                    </Option>
                  ))}
                </Select>

                <Button
                  onClick={clearFilters}
                  aria-label="Clear all active filters"
                >
                  Clear
                </Button>
              </div>
            </div>

            <div className="mn-panel-body">
              <Table
                className="mn-table"
                columns={COLUMNS}
                dataSource={filteredModalities}
                rowKey="en_id"
                size="small"
                aria-label="Nodes management table"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} nodes`,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showLessItems: true,
                }}
                locale={{
                  emptyText: searchText || selectedUnit
                    ? 'No nodes match the current filters'
                    : 'No nodes found'
                }}
              />
            </div>
          </div>
        </div>

        <EditNode
          visible={editModalVisible}
          nodeData={selectedModality}
          onCancel={handleCloseEditModal}
          onSubmit={handleEditSubmit}
          unitsList={centers}
        />
      </Spin>
    </ErrorBoundary>
  );
};

export default ModalityList;
